import React, { useEffect, useMemo, useState, useRef, Fragment, createElement, Component } from 'react';
import { ClockIcon, ShoppingCartIcon, MessageCircleIcon, PlayIcon } from 'lucide-react';
import { useMidnightCountdown } from '../hooks/useMidnightCountdown';
import { useMetaPixel } from '../hooks/useMetaPixel';
import { LegalLinks } from '../components/LegalLinks';
import { FloatingWhatsApp } from '../components/FloatingWhatsApp';
import { ExitIntentModal } from '../components/ExitIntentModal';
import { WhatsAppExitModal } from '../components/WhatsAppExitModal';
import { LiveReactions } from '../components/LiveReactions';
import { RollingNumber } from '../components/RollingNumber';
import { SalesNotifications } from '../components/SalesNotifications';
import { buildWhatsAppLink } from '../utils/whatsappDeck';
export function VslPage() {
  const {
    hours,
    minutes,
    seconds
  } = useMidnightCountdown();
  // Meta Pixel: init + PageView fire automatically on mount.
  const {
    trackStandardEvent,
    trackCustomEvent
  } = useMetaPixel();
  const pad = (n: number) => n < 10 ? `0${n}` : n;
  // Which VSL route are we on? Each page has its own video + WhatsApp deck.
  const pageKey = (() => {
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    // Check the most specific routes first (each contains the shorter prefixes).
    // Contact variant: duplicate of /vsl-3-2 with no offer reveal, just a
    // WhatsApp contact CTA (checked first so it never matches another route).
    if (path.includes('vsl-contact')) return 'vsl-contact';
    // /vsl-short-4min-49 — same video/timing as /vsl-short-4min but on the
    // $49/mo subscription offer (checked first; it contains 'vsl-short-4min').
    if (path.includes('vsl-short-4min-49')) return 'vsl-short-4min-49';
    // Duplicate of /vsl-4-2 ($97 offer) with a 4:44 reveal + 5:00 sales pops.
    if (path.includes('vsl-short-4min')) return 'vsl-short-4min';
    if (path.includes('vsl-3-2-shorter-demo')) return 'vsl-3-2-shorter-demo';
    // Duplicate of /vsl-3-2 (same video + $147 offer) on a dedicated lead path.
    if (path.includes('vsl-3-lead-2-147')) return 'vsl-3-lead-2-147';
    if (path.includes('vsl-3-3')) return 'vsl-3-3';
    if (path.includes('vsl-3-2')) return 'vsl-3-2';
    if (path.includes('vsl-3-shorter-demo')) return 'vsl-3-shorter-demo';
    if (path.includes('vsl-3-147')) return 'vsl-3-147';
    if (path.includes('vsl-3')) return 'vsl-3';
    // Duplicate of /vsl-4 but at the $97 offer (must be checked before vsl-4).
    if (path.includes('vsl-4-2')) return 'vsl-4-2';
    // Duplicate of /vsl-4-2 at the $49/mo subscription offer (before vsl-4).
    if (path.includes('vsl-4-3-s')) return 'vsl-4-3-s';
    // Duplicate of /vsl-3-3 (same video + $147 offer, 21-min reveal).
    if (path.includes('vsl-4')) return 'vsl-4';
    if (path.includes('vsl-2')) return 'vsl-2';
    return 'vsl';
  })();
  // Video per route: each VSL page plays its own PandaVideo embed.
  // /vsl now plays a dedicated PandaVideo (previously Wistia), so it renders
  // the PandaVideo branch with postMessage-driven progress gating.
  const pandaIds: Record<string, string> = {
    vsl: '66729d57-678b-4ec4-b0ff-30d99a733e08',
    'vsl-2': '35fe2843-e74c-49e6-98e3-a3c50bf0cdb5',
    'vsl-3': '9c98036b-bb98-462c-8e8a-bed4c09d74fd',
    // /vsl-3 video, run at the $147 offer.
    'vsl-3-147': '9c98036b-bb98-462c-8e8a-bed4c09d74fd',
    // Shorter cut of /vsl-3 — its own video + 18-min reveal gate.
    'vsl-3-shorter-demo': 'fcc70287-da57-4150-a526-2dda5e2c5422',
    'vsl-3-2': 'de37c814-a113-418e-b49e-d2612f5d3c12',
    // /vsl-contact — no offer reveal; plays its own dedicated PandaVideo.
    'vsl-contact': 'f3ca9ac9-600e-45ef-98f9-2a3ce6259b57',
    // /vsl-3-3 — duplicate of /vsl-3-2 ($147 offer) with its own dedicated video.
    'vsl-3-3': '866b50bf-1590-4a8f-a4d6-f0d233d9b3e2',
    // NOTE: /vsl-4 is intentionally NOT here — it uses a Wistia video instead
    // (see wistiaIds below), so it renders the Wistia branch, not PandaVideo.
    // /vsl-3-lead-2-147 — $147 offer (like /vsl-3-2) but its own dedicated video.
    'vsl-3-lead-2-147': '866b50bf-1590-4a8f-a4d6-f0d233d9b3e2',
    'vsl-3-2-shorter-demo': 'd8a49da9-3753-4e69-82d9-afbde4e190c9',
    // /vsl-short-4min — its own dedicated PandaVideo ($97 offer, 4:44 reveal).
    'vsl-short-4min': '9cef39be-6f21-48af-be7a-5aa12e1a2e9e',
    // /vsl-short-4min-49 — same video as /vsl-short-4min, on the $49/mo offer.
    'vsl-short-4min-49': '9cef39be-6f21-48af-be7a-5aa12e1a2e9e'
  };
  const usePanda = pageKey in pandaIds;
  const pandaId = pandaIds[pageKey] || '';
  const pandaSrc = 'https://player-vz-cf0b871f-7fe.tv.pandavideo.com/embed/?v=' + pandaId;
  // Wistia routes: any route NOT in pandaIds renders the Wistia branch with its
  // own media id. /vsl-4 (and its $97 duplicate /vsl-4-2) play rjnogmuf71.
  const wistiaIds: Record<string, string> = {
    'vsl-4': 'rjnogmuf71',
    'vsl-4-2': 'rjnogmuf71',
    // NOTE: /vsl-short-4min is intentionally NOT here — it now plays its own
    // dedicated PandaVideo (see pandaIds), so it renders the PandaVideo branch.
    'vsl-4-3-s': 'rjnogmuf71'
  };
  const mediaId = wistiaIds[pageKey] || 'yw45oz4l0j';
  // Offer config per route. The $147 routes (/vsl-3-2, its shorter-demo variant,
  // and /vsl-3-147) run the discounted offer and point every CTA at the $147
  // Fanbasis checkout; all other routes keep the $297 offer.
  // $147 routes.
  const is147Offer = pageKey === 'vsl-3-2' || pageKey === 'vsl-3-2-shorter-demo' || pageKey === 'vsl-3-147' || pageKey === 'vsl-3-lead-2-147' || pageKey === 'vsl-3-3' || pageKey === 'vsl-4';
  // $97 route: /vsl-4-2 (duplicate of /vsl-4 that opens the $97 offer) and its
  // faster-reveal duplicate /vsl-short-4min.
  const is97Offer = pageKey === 'vsl-4-2' || pageKey === 'vsl-short-4min';
  // $49/mo subscription routes: /vsl-4-3-s and /vsl-short-4min-49.
  const isSubOffer = pageKey === 'vsl-4-3-s' || pageKey === 'vsl-short-4min-49';
  // "isAltOffer" = any non-standard offer ($49/mo, $97, or $147); drives the
  // social-proof notifications and the post-pitch exit popup.
  const isAltOffer = is147Offer || is97Offer || isSubOffer;
  // The new hero headline/subheadline copy is exclusive to the /vsl-4 family
  // (/vsl-4, /vsl-4-2, /vsl-4-3-s). Every other route keeps the original copy.
  const isVsl4Family = pageKey === 'vsl-4' || pageKey === 'vsl-4-2' || pageKey === 'vsl-4-3-s' || pageKey === 'vsl-short-4min' || pageKey === 'vsl-short-4min-49';
  // Contact variant: same page as /vsl-3-2 but with NO offer reveal (the timer
  // + offer section never appear) and a WhatsApp contact CTA under the video.
  const isContact = pageKey === 'vsl-contact';
  const priceLabel = isSubOffer ? '$49/mo' : is97Offer ? '$97' : is147Offer ? '$147' : '$297';
  const priceValue = isSubOffer ? 49 : is97Offer ? 97 : is147Offer ? 147 : 297;
  const waPrice = isSubOffer ? '49' : is97Offer ? '97' : is147Offer ? '147' : '297';
  const checkoutUrl = isSubOffer ? 'https://www.fanbasis.com/agency-checkout/true-marketer/rMLgk' : is97Offer ? 'https://www.fanbasis.com/agency-checkout/true-marketer/l2xj7' : is147Offer ? 'https://www.fanbasis.com/agency-checkout/true-marketer/gQ88l' : 'https://www.fanbasis.com/agency-checkout/true-marketer/D9Vl5';
  const installmentLabel = is97Offer ? '$24.25' : is147Offer ? '$38' : '$75.75';
  // Secondary price line. The subscription shows a "cancel anytime" note instead
  // of the one-time "4 payments of X" installment line.
  const priceSubLabel = isSubOffer ? 'Cancela cuando quieras' : `O 4 pagos de ${installmentLabel}`;
  // WhatsApp lead distribution (Shuffle Deck): each page keeps its own deck and
  // the visitor is dealt ONE rep for the whole visit, so the floating button
  // and the exit popup always reach the same number.
  const {
    floatingWaLink,
    exitWaLink
  } = useMemo(() => {
    return {
      floatingWaLink: buildWhatsAppLink(pageKey,
      // Info-seeking opener for the always-on floating button (no price, so it
      // reads as someone wanting information rather than ready to buy).
      // Encoded with encodeURIComponent so the 👋 emoji and accents round-trip
      // correctly (hand-encoding rendered the emoji as a replacement glyph).
      encodeURIComponent('Hola 👋 ¿Me puedes dar más info sobre el 48hrs AI Cash Machine?')),
      exitWaLink: buildWhatsAppLink(pageKey, 'Hola%20Gabriel.%20Estoy%20ocupado(a)%20ahorita%20pero%20no%20quiero%20perderme%20el%20entrenamiento%20de%2048hrs%20Cash%20Machine.%20Me%20lo%20puedes%20mandar%3F')
    };
  }, [pageKey, waPrice]);
  // Footer disclaimer popup (bottom sheet).
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  // Live "how many people are watching" counter. It moves ONE step at a time so
  // the scoreboard rolls smoothly. For the first 3 minutes it drifts up and down
  // freely within 13–35; after 3 minutes it stops the free decreasing and only
  // ever dips by a single number (never two drops in a row), trending upward.
  const VIEWER_MIN = 13;
  const VIEWER_MAX = 35;
  const RAMP_LOCK_MS = 3 * 60 * 1000; // after 3 min, lock the downward drift
  const [liveViewers, setLiveViewers] = useState(17);
  const lastDeltaRef = useRef(0);
  useEffect(() => {
    const startedAt = Date.now();
    let timer: number;
    const tick = () => {
      const locked = Date.now() - startedAt >= RAMP_LOCK_MS;
      setLiveViewers(prev => {
        let delta: number;
        if (locked) {
          // Phase 2: trend up; allow a single -1 dip, but never back-to-back,
          // and never below the floor.
          const r = Math.random();
          if (r < 0.6) delta = 1;else if (r < 0.85) delta = 0;else delta = lastDeltaRef.current < 0 ? 1 : -1;
        } else {
          // Phase 1: gentle ±1 random walk with a light upward bias.
          const r = Math.random();
          if (r < 0.45) delta = 1;else if (r < 0.85) delta = -1;else delta = 0;
        }
        let next = prev + delta;
        if (next > VIEWER_MAX) next = VIEWER_MAX - 1;
        if (next < VIEWER_MIN) next = VIEWER_MIN + 1;
        lastDeltaRef.current = next - prev;
        return next;
      });
      // Steady, slightly irregular cadence so it reads like a live count.
      timer = window.setTimeout(tick, 1800 + Math.random() * 1600);
    };
    timer = window.setTimeout(tick, 1800 + Math.random() * 1600);
    return () => window.clearTimeout(timer);
  }, []);
  // PERF: warm up connections to the external hosts the first screen needs
  // (video, image CDN, fonts) and preload the above-the-fold trust badge.
  // Purely a loading hint — no visual or functional change.
  useEffect(() => {
    const add = (rel: string, href: string, opts: {
      crossOrigin?: string;
      as?: string;
    } = {}) => {
      if (document.querySelector(`link[rel="${rel}"][href="${href}"]`)) return;
      const l = document.createElement('link');
      l.rel = rel;
      l.href = href;
      if (opts.crossOrigin) l.crossOrigin = opts.crossOrigin;
      if (opts.as) l.as = opts.as;
      document.head.appendChild(l);
    };
    add('preconnect', 'https://fast.wistia.com');
    add('preconnect', 'https://player-vz-cf0b871f-7fe.tv.pandavideo.com');
    add('preconnect', 'https://cdn.magicpatterns.com');
    add('preconnect', 'https://fonts.googleapis.com');
    add('preconnect', 'https://fonts.gstatic.com', {
      crossOrigin: 'anonymous'
    });
    add('preload', 'https://cdn.magicpatterns.com/uploads/ex6bfN8HifSFSTrkA5aMXZ/TRUSTED_BADGE.png', {
      as: 'image'
    });
  }, []);
  // Fire ViewContent once when the /vsl page loads.
  // Stable product identity so Meta can match events to a single catalog item,
  // which improves Event Match Quality and optimization toward real buyers.
  const PRODUCT_ID = '48hrs-ai-cash-machine';
  useEffect(() => {
    trackStandardEvent('ViewContent', {
      content_name: '48 Hrs AI Cash Machine — VSL',
      content_category: 'VSL',
      content_ids: [PRODUCT_ID],
      content_type: 'product',
      value: priceValue,
      currency: 'USD'
    });
  }, [trackStandardEvent, priceValue]);
  const goToCheckout = () => {
    // Fire AddToCart + InitiateCheckout before redirecting to Fanbasis.
    // InitiateCheckout is a high-value standard event Meta optimizes against,
    // so sending it (with value/currency/content) sharpens audience quality.
    const productData = {
      content_name: '48 Hrs AI Cash Machine',
      content_ids: [PRODUCT_ID],
      content_type: 'product',
      value: priceValue,
      currency: 'USD'
    };
    trackStandardEvent('AddToCart', productData);
    trackStandardEvent('InitiateCheckout', {
      ...productData,
      num_items: 1
    });
    // If this page is running inside an iframe (e.g. embedded in an ad or
    // wrapper), navigating `window.location` would only redirect the framed
    // page — and Fanbasis blocks framing, so the click appeared to do nothing.
    // Break out to the top window so the checkout loads in the full tab.
    const inFrame = typeof window !== 'undefined' && window.top !== window.self;
    if (inFrame) {
      try {
        ;
        (window.top ?? window).location.href = checkoutUrl;
        return;
      } catch {
        // Cross-origin access to window.top is blocked — fall back to a
        // top-level navigation via a synthesized target="_top" link.
        const a = document.createElement('a');
        a.href = checkoutUrl;
        a.target = '_top';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }
    }
    window.location.href = checkoutUrl;
  };
  const handleCheckout = (e: React.MouseEvent) => {
    e.preventDefault();
    goToCheckout();
  };
  const ImageFallback = ({
    text
  }: {
    text: string;
  }) => <div className="w-full aspect-square rounded-xl flex flex-col items-center justify-center text-center p-4 bg-gradient-to-br from-[#f0f9ff] to-[#cffafe] border border-[#67e8f9] font-['Anton'] text-[#0e7490] text-xl tracking-wide" data-id="element-739" data-mp="pg1wk8qf2rcc">
      {text.split('\\n').map((line, i) => <Fragment key={i} data-id="element-740" data-mp="2i4rjo4kfyhv2" data-mpi={i}>
          {line}
          <br data-id="element-741" data-mp="24clbb4nj5f66" />
        </Fragment>)}
    </div>;
  // VSL GATE: show only headline + video until video playback reaches the reveal
  // mark, then reveal the full sales page (and pop the floating header).
  // The "shorter-demo" variants play a shorter cut, so their entire offer opens
  // at 18:00 instead of the standard 27:27.
  const REVEAL_SECONDS = pageKey === 'vsl-3-2-shorter-demo' || pageKey === 'vsl-3-shorter-demo' ? 18 * 60 : pageKey === 'vsl-short-4min' || pageKey === 'vsl-short-4min-49' ? 4 * 60 + 44 // /vsl-short-4min(-49) offer opens at 4:44 (284s)
  : pageKey === 'vsl-4' || pageKey === 'vsl-4-2' || pageKey === 'vsl-4-3-s' ? 4 * 60 + 40 // /vsl-4, /vsl-4-2, /vsl-4-3-s offer opens at 4:40 (280s)
  : pageKey === 'vsl-3-3' ? 21 * 60 // /vsl-3-3 pitch lands at 21:00
  : 27 * 60 + 27; // 1080s vs 280s vs 1260s vs 1647s
  // After 7 minutes of playback, show a "next step opens soon" sub-headline.
  const STEP_HINT_SECONDS = 7 * 60; // 420s
  // CONTACT VARIANT: the "people reached out via WhatsApp" notifications start
  // once playback reaches 7 min 10 sec.
  const CONTACT_SALES_SECONDS = 7 * 60 + 10; // 430s
  // Arm the exit-intent recovery popup after 25 minutes of playback.
  const EXIT_ARM_SECONDS = 25 * 60; // 1500s
  // Fire the "27Min" Facebook pixel event at 27 minutes (kept separate so the
  // event name/timing stays consistent with existing tracking).
  const EVENT_27MIN_SECONDS = 27 * 60; // 1620s
  // Start the social-proof sales notifications exactly when the pitch/offer
  // opens (the reveal mark — 18 min on shorter-demo routes, 27:27 otherwise), so
  // no route ever shows a sale before the pitch begins. EXCEPTION: /vsl-short-4min
  // opens its offer at 4:44 but holds the sales pops until 5:00.
  const SALES_START_SECONDS = pageKey === 'vsl-short-4min' || pageKey === 'vsl-short-4min-49' ? 5 * 60 : REVEAL_SECONDS;
  const [revealed, setRevealed] = useState(false);
  const [showStepHint, setShowStepHint] = useState(false);
  // CONTACT VARIANT: the green WhatsApp button only appears once playback
  // reaches the 7-minute mark (STEP_HINT_SECONDS).
  const [showContactButton, setShowContactButton] = useState(false);
  // Exit-intent popup is only armed after 25 minutes of playback.
  const [exitArmed, setExitArmed] = useState(false);
  const exitArmedRef = useRef(false);
  const min27FiredRef = useRef(false);
  const armExitPopup = () => {
    if (exitArmedRef.current) return;
    exitArmedRef.current = true;
    setExitArmed(true);
  };
  const fire27MinEvent = () => {
    if (min27FiredRef.current) return;
    min27FiredRef.current = true;
    trackCustomEvent('27Min');
  };
  // Arm the sales-notification loop once playback crosses the 18-min mark.
  const [salesArmed, setSalesArmed] = useState(false);
  const salesArmedRef = useRef(false);
  const armSales = () => {
    if (salesArmedRef.current) return;
    salesArmedRef.current = true;
    setSalesArmed(true);
  };
  // Preview: ?exitdemo=1 opens the exit-intent recovery popup immediately so it
  // can be reviewed without waiting for the 25-min playback trigger.
  const [exitDemo] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('exitdemo') === '1';
    } catch {
      return false;
    }
  });
  // Live reactions: armed once playback begins and left on, so the reaction
  // waves keep arriving naturally (~every 30s) for the whole video.
  const [reactionsActive, setReactionsActive] = useState(false);
  const playStartedRef = useRef(false);
  const startReactionBurst = () => {
    if (playStartedRef.current) return;
    playStartedRef.current = true;
    setReactionsActive(true);
  };
  // Persist the furthest playback point reached (per video) so a viewer who
  // leaves and returns resumes the offer timeline instead of restarting it.
  const watchKey = `vsl_watch_max_${usePanda ? pandaId : mediaId}`;
  const maxWatchedRef = useRef(0);
  useEffect(() => {
    const w = window as any;
    // Restore the furthest watched point saved on a previous visit.
    try {
      const saved = parseInt(localStorage.getItem(watchKey) || '0', 10) || 0;
      if (saved > maxWatchedRef.current) maxWatchedRef.current = saved;
    } catch {}
    // Apply playback progress to the gate, anchored to the furthest point ever
    // reached — so leaving at 5 min and returning resumes the timeline there
    // rather than restarting. Returns true once the offer should be revealed.
    const applyProgress = (t: number): boolean => {
      if (t > maxWatchedRef.current) {
        maxWatchedRef.current = t;
        try {
          localStorage.setItem(watchKey, String(Math.floor(t)));
        } catch {}
      }
      const eff = Math.max(t, maxWatchedRef.current);
      if (eff > 0) startReactionBurst();
      // CONTACT VARIANT: no offer, no timer — but reveal the WhatsApp contact
      // button once playback crosses the 7-minute mark, then skip every other
      // reveal/offer trigger.
      if (isContact) {
        if (eff >= STEP_HINT_SECONDS) setShowContactButton(true);
        // Pop notifications begin at 7:10 of playback.
        if (eff >= CONTACT_SALES_SECONDS) armSales();
        return false;
      }
      if (eff >= STEP_HINT_SECONDS) setShowStepHint(true);
      if (eff >= EXIT_ARM_SECONDS) armExitPopup();
      if (eff >= EVENT_27MIN_SECONDS) fire27MinEvent();
      if (eff >= SALES_START_SECONDS) armSales();
      if (eff >= REVEAL_SECONDS) {
        setRevealed(true);
        return true;
      }
      return false;
    };
    // Immediately seed the gate from the restored progress on mount.
    applyProgress(0);
    // /vsl plays a PandaVideo embed: drive the gate from PandaVideo's
    // postMessage playback events instead of the Wistia API.
    if (usePanda) {
      const onPandaMessage = (e: MessageEvent) => {
        let data: any = e.data;
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch {
            return;
          }
        }
        if (!data || typeof data !== 'object') return;
        const msg = String(data.message || data.event || '');
        if (!/time|progress|play/i.test(msg)) return;
        const t = Number(data.currentTime ?? data.time ?? data.seconds ?? 0);
        if (Number.isFinite(t) && t > 0) applyProgress(t);
      };
      window.addEventListener('message', onPandaMessage);
      return () => {
        window.removeEventListener('message', onPandaMessage);
      };
    }
    // /vsl-2 and /vsl-3 keep the Wistia player + progress gating.
    const ensureScript = (src: string, asModule = false) => {
      if (document.querySelector(`script[src="${src}"]`)) return;
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      if (asModule) s.type = 'module';
      document.body.appendChild(s);
    };
    ensureScript('https://fast.wistia.com/player.js');
    ensureScript(`https://fast.wistia.com/embed/${mediaId}.js`, true);
    // Primary path: classic Wistia Player API timechange binding.
    w._wq = w._wq || [];
    w._wq.push({
      id: mediaId,
      onReady: (video: any) => {
        video.bind('timechange', (t: number) => {
          applyProgress(t);
        });
      }
    });
    // Robust fallback: poll the current playback time from whichever API is
    // available (classic API or the <wistia-player> web component element).
    const interval = window.setInterval(() => {
      let t = 0;
      try {
        const api = w.Wistia?.api?.(mediaId);
        if (api && typeof api.time === 'function') t = api.time();
      } catch {}
      if (!t) {
        const el = document.querySelector(`wistia-player[media-id="${mediaId}"]`) as any;
        if (el && typeof el.currentTime === 'number') t = el.currentTime;
      }
      if (applyProgress(t)) window.clearInterval(interval);
    }, 1000);
    return () => {
      window.clearInterval(interval);
    };
  }, [REVEAL_SECONDS, STEP_HINT_SECONDS, CONTACT_SALES_SECONDS, EXIT_ARM_SECONDS, mediaId, watchKey, usePanda, isContact]);
  return <div className={`min-h-screen flex flex-col text-[#0f172a] font-['Inter'] selection:bg-[#36D9F4]/40 ${revealed ? "bg-[url('https://cdn.magicpatterns.com/uploads/n7ea57jK586HPiiMHqCkL9/private_openrouter-generated-image-1781212646060.png')] bg-cover bg-center bg-fixed" : 'bg-gradient-to-b from-[#0f172a] from-50% via-[#163e63] to-[#1d6fa5] bg-fixed'}`} data-id="element-742" data-mp="2wwuzm560hx62">
      {/* TOP HEADER — trust badge + Gabriel Beltrán signature */}
      <header className="bg-[#0f172a] border-b border-white/10 px-5 pt-3 pb-2.5" data-id="element-743" data-mp="295zaxgwuri31">
        <div className="max-w-[860px] mx-auto flex flex-col items-center" data-id="element-744" data-mp="246cvd0tjxpr2">
          <img src="https://cdn.magicpatterns.com/uploads/ex6bfN8HifSFSTrkA5aMXZ/TRUSTED_BADGE.png" alt="Calificación de 5 estrellas en Trustpilot — Confiado por más de 1000 clientes verificados" className="w-full max-w-[175px] h-auto" data-id="element-745" data-mp="248fk7hiyxcu7" />
        </div>
      </header>

      {/* VSL HERO — headline + video, always visible at the top.
                                                                                                                                                                                                    Transparent on the gated view so the fade-to-blue background shows. */}
      <section className={`px-5 py-10 text-center ${revealed ? 'bg-[#0f172a]' : 'bg-transparent'}`} data-id="element-746" data-mp="3s20y1h1be4p5">
        <div className="w-full max-w-[860px] mx-auto" data-id="element-747" data-mp="vq4s72jq88d">
          {isVsl4Family ? <>
              <h1 className="font-['Anton'] uppercase tracking-[0.5px] text-[clamp(26px,4.6vw,48px)] leading-[1.05] text-white mb-3" data-id="element-748" data-mp="4y4m2mqkgzxj">
                Deja De Adivinar Qué Vender: Instala Tu{' '}
                <span className="text-[#36D9F4]" data-id="element-749" data-mp="39ddtr0qypjcm">48Hrs AI Cash Machine</span> Y
                Lanza Tu Producto Digital En Menos De 2 Días.
              </h1>
              <p className="text-[13px] sm:text-[16px] text-white/70 mb-7 max-w-[680px] mx-auto leading-relaxed" data-id="element-750" data-mp="2u9t7tpluukmp">
                Sin seguidores, sin meses de estudio y sin "cansancio mental".
                Te ayudamos a construir un{' '}
                <b className="text-white font-semibold" data-id="element-751" data-mp="341qk6zolnin0">
                  producto real, una página de ventas y anuncios
                </b>{' '}
                listos para validar con IA mientras otros siguen "intentando"
                entender el marketing.
              </p>
            </> : <>
              <p className="text-[11px] sm:text-[13px] font-bold uppercase tracking-[2px] text-[#36D9F4] mb-3" data-id="element-752" data-mp="381r5n4a48u3k">
                Entrenamiento Gratis · 48 Hrs AI Cash Machine
              </p>
              <h1 className="font-['Anton'] uppercase tracking-[0.5px] text-[clamp(26px,4.6vw,48px)] leading-[1.05] text-white mb-3" data-id="element-753" data-mp="3i0ykxx0euytb">
                Cómo Lanzar Tu Primer Producto Digital Con IA En{' '}
                <span className="text-[#36D9F4]" data-id="element-754" data-mp="1no0gvv5b0yef">48 Horas</span> — Aunque Hoy
                Empieces De Cero Y No Sepas Nada De Tecnología.
              </h1>
              <p className="text-[13px] sm:text-[16px] text-white/70 mb-7 max-w-[680px] mx-auto leading-relaxed" data-id="element-755" data-mp="83hp8vgjksge">
                Sin experiencia. Sin código. Sin equipo. Mi agente de IA,{' '}
                <b className="text-white font-semibold" data-id="element-756" data-mp="3ui11ali1nvjp">HEYLLOYD,</b> hace el
                trabajo pesado. Tú diriges.
              </p>
            </>}
          <div className="relative w-full overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.45)] bg-black" data-id="element-757" data-mp="3gl4dksx82u71">
            {usePanda ? <div className="relative w-full" style={{
            paddingTop: '56.25%'
          }} data-id="element-758" data-mp="36ktz74zgbgqa">
                <iframe key={`panda-${pandaId}`} id={`panda-${pandaId}`} src={pandaSrc} title="48 Hrs AI Cash Machine — VSL" className="absolute inset-0 w-full h-full border-0" allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture" allowFullScreen fetchPriority="high" data-id="element-759" data-mp="3bjoq6u7koblh" />
              </div> : <div key={`wistia-${mediaId}`} className="relative w-full" style={{
            paddingTop: '56.25%'
          }} dangerouslySetInnerHTML={{
            __html: "<style>{`wistia-player[media-id='" + mediaId + "']:not(:defined){background:center / contain no-repeat url('https://fast.wistia.com/embed/medias/" + mediaId + '/swatch\');display:block;filter:blur(5px);padding-top:56.25%;}`}</style><wistia-player media-id="' + mediaId + '" aspect="1.7777777777777777" style="position:absolute;inset:0;width:100%;height:100%;"></wistia-player>'
          }} data-id="element-760" data-mp="18b4vh14ehhff" />}
            {/* Live-stream style floating reactions over the video */}
            <LiveReactions active={reactionsActive} data-id="element-761" data-mp="1harx9o11kp90" />
          </div>

          {/* Play nudge — original "Dale play" line on the non-/vsl-4 routes */}
          {!isVsl4Family && !revealed}

          {/* LIVE studio indicator under the video */}
          <div className="mt-4 inline-flex items-center gap-2" data-id="element-762" data-mp="251nlyca7b4c1">
            <span className="relative flex h-4 w-4" data-id="element-763" data-mp="250478q4711cd">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#FD0200] opacity-90" data-id="element-764" data-mp="2datb8ky1lj0s" />
              <span className="relative inline-flex h-4 w-4 rounded-full bg-[#FD0200] shadow-[0_0_8px_rgba(253,2,0,0.8)]" data-id="element-765" data-mp="2hcy43hgo2bmd" />
            </span>
            <span className="inline-flex items-center gap-1.5 text-[12px] sm:text-[13px] text-[#FD0200]" aria-live="polite" data-id="element-766" data-mp="2md2npgcvfmgt">
              <RollingNumber value={liveViewers} className="font-['Anton'] text-[15px] sm:text-[17px] text-[#FD0200]" data-id="element-767" data-mp="2w9fd421dve5r" />
              <span className="font-bold uppercase tracking-[1.5px]" data-id="element-768" data-mp="309zbf8xp0nth">
                mirando en vivo
              </span>
            </span>
          </div>

          {/* CONTACT VARIANT: green WhatsApp contact button under the live
           indicator — appears once playback reaches 7 min. */}
          {isContact && showContactButton && <div className="mt-5 flex justify-center" data-id="element-769" data-mp="3f1882200j9g4">
              <a href={floatingWaLink} target="_blank" rel="noopener noreferrer" onClick={() => {
            // Fire the Meta Pixel standard "Lead" event when a /vsl-contact
            // viewer clicks "Contáctanos para más información".
            trackStandardEvent('Lead', {
              content_name: '48 Hrs AI Cash Machine — Contact',
              content_category: 'VSL Contact',
              source: 'contact_button'
            });
            trackCustomEvent('Contact', {
              source: 'contact_button'
            });
          }} style={{
            backgroundImage: 'linear-gradient(180deg, #2ee06f 0%, #25D366 55%, #21bd5c 100%)',
            boxShadow: '0 3px 0 #1a9e4b, 0 6px 14px rgba(16,140,62,0.28), inset 0 1px 0 rgba(255,255,255,0.35)'
          }} className="group relative isolate inline-flex items-center justify-center gap-2.5 w-full max-w-[420px] text-white font-extrabold text-[16px] sm:text-[18px] tracking-tight px-6 py-4 rounded-full overflow-hidden transition-all duration-150 hover:-translate-y-px hover:brightness-[1.02] active:translate-y-[1px] active:[box-shadow:0_1px_0_#1a9e4b,0_3px_8px_rgba(16,140,62,0.25),inset_0_1px_0_rgba(255,255,255,0.3)]" data-id="element-770" data-mp="3jy8ck8h7s454">
                {/* Subtle top gloss highlight */}
                <span aria-hidden="true" className="pointer-events-none absolute inset-x-[8%] top-[3px] h-[38%] rounded-full bg-gradient-to-b from-white/25 to-transparent" data-id="element-771" data-mp="1i63y2c4upg5o" />
                <svg viewBox="0 0 24 24" className="relative z-10 h-6 w-6 shrink-0 fill-white" aria-hidden="true" data-id="element-772" data-mp="19dafo5u3y0nl">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" data-id="element-773" data-mp="1x2laz7sov2uc" />
                </svg>
                <span className="relative z-10" data-id="element-774" data-mp="gbu1xioljveo">
                  Contáctanos para más información
                </span>
              </a>
            </div>}

          {/* Appears after 7 min of playback, until the offer reveals */}
          {showStepHint && !revealed && <p className="mt-5 inline-flex items-center gap-2 text-[13px] sm:text-[15px] font-semibold text-[#36D9F4] animate-pulse" data-id="element-775" data-mp="3jqzuxltwr869">
              <ClockIcon className="w-4 h-4 shrink-0" data-id="element-776" data-mp="25xkqle9ue05m" />
              El próximo paso se abrirá en 15 minutos ...
            </p>}

          {/* "No time?" WhatsApp shortcut for interested viewers */}
        </div>
      </section>

      {/* OFFER — appears under the video once it reaches the reveal mark */}
      {revealed && <>
          <style data-id="element-777" data-mp="1rwi26uvdkdrj">{`@keyframes vslPop{0%{transform:scale(0.92);opacity:0}55%{transform:scale(1.04)}100%{transform:scale(1);opacity:1}}.vsl-pop{animation:vslPop .55s cubic-bezier(.34,1.56,.64,1) both}`}</style>
          {/* FLOATING OFFER BAR */}
          <div className="vsl-pop sticky top-0 z-50 bg-gradient-to-b from-[#FD0200] to-[#AA0E13] text-[#ffffff] border-b border-[#AA0E13] shadow-[0_6px_24px_rgba(170,14,19,0.45)]" data-id="element-778" data-mp="1e5wgyzig7rqh">
            <div className="max-w-[1180px] mx-auto px-4 h-11 flex items-center justify-between gap-3" data-id="element-779" data-mp="194p9z2o4qwkv">
              <div className="flex items-center gap-2 min-w-0" data-id="element-780" data-mp="149jo23uq9hws">
                <span className="hidden sm:inline font-semibold text-[12.5px] tracking-wide truncate" data-id="element-781" data-mp="z9xa1s72rxmx">
                  48 Hrs AI Cash Machine
                </span>
                <span className="hidden sm:inline text-white/50" data-id="element-782" data-mp="ukmit2olf2xr">·</span>
                <span className="font-['Anton'] text-[14px] leading-none" data-id="element-783" data-mp="uhqm1hohekaw">
                  {priceLabel}
                </span>
                <span className="text-[11px] text-white/70 line-through leading-none" data-id="element-784" data-mp="kldtwo8l4ei9">
                  $9,370
                </span>
              </div>
              <div className="flex items-center gap-3 shrink-0" data-id="element-785" data-mp="18dktrxdir3mf">
                <div className="flex items-center gap-1.5 text-[12.5px] tabular-nums" data-id="element-786" data-mp="1d99krcbh3c74">
                  <ClockIcon className="w-3.5 h-3.5 text-white/80" data-id="element-787" data-mp="zjzbw562568s" />
                  <span className="font-semibold tracking-tight" data-id="element-788" data-mp="13krlx0mr7f64">
                    {pad(hours)}:{pad(minutes)}:{pad(seconds)}
                  </span>
                </div>
                <a href="#" onClick={handleCheckout} className="inline-flex items-center rounded-full bg-white text-[#dc2626] text-[11px] font-bold uppercase tracking-wide px-3 py-1 hover:bg-white/90 transition-colors" data-id="element-789" data-mp="1r89bcpijq15x">
                  Obtener acceso
                </a>
              </div>
            </div>
          </div>

          {/* HERO SECTION */}
          <header className="bg-transparent border-b border-[#e2e8f0] pb-12" data-id="element-790" data-mp="3qg96z9hl3hn7">
            <div className="max-w-[1180px] mx-auto px-5" data-id="element-791" data-mp="3lho75b68uzza">
              <div className="relative left-1/2 right-1/2 -mx-[50vw] w-screen mb-10 bg-gradient-to-b from-[#1e293b] to-[#0f172a] overflow-hidden" data-id="element-792" data-mp="3lkaou3mumvv8">
                <div className="absolute inset-0 bg-[radial-gradient(80%_120%_at_50%_0%,rgba(54,217,244,0.18)_0%,transparent_60%)] pointer-events-none" data-id="element-793" data-mp="2subv3uxpmak8" />
                <div className="relative max-w-[920px] mx-auto px-5 py-12 sm:py-16" data-id="element-794" data-mp="37oq6paey2hov">
                  <h1 className="relative font-['Anton'] text-[clamp(28px,4.8vw,54px)] leading-[1.02] text-center uppercase tracking-[0.5px] text-[#ffffff]" data-id="element-795" data-mp="32qeuwu539x40">
                    Genera Tu Primer Ingreso con AI en las{' '}
                    <span className="text-[#36D9F4]" data-id="element-796" data-mp="i3trnzk6k9c0">Próximas 48 Horas</span> —
                    Sin Experiencia y Sin Adivinar
                  </h1>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-8 lg:gap-10 items-start" data-id="element-797" data-mp="2sqpc9gc6wfil">
                {/* LEFT COLUMN */}
                <div className="relative rounded-3xl border border-[#9ca3af] shadow-[0_10px_30px_rgba(0,0,0,0.12)] bg-white px-5 sm:px-7 pb-8 mt-20 sm:mt-28" data-id="element-798" data-mp="31psuki3j8qw1">
                  <div className="-mt-28 sm:-mt-36 mb-4 flex justify-center" data-id="element-799" data-mp="36mszuqsk5pm3">
                    <img src="https://cdn.magicpatterns.com/uploads/gnQatCwJe9kqMyVCDBjSpQ/Whole_course.png" alt="48 Hrs AI Cash Machine — Bundle Completo" className="max-w-full lg:max-w-[620px] h-auto" onError={e => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }} data-id="element-800" data-mp="3blvynthy2zlm" />
                    <div className="hidden w-full max-w-[480px]" data-id="element-801" data-mp="3uezu9keq3miv">
                      <ImageFallback text="WHOLE BUNDLE" data-id="element-802" data-mp="8dab5u502d0s" />
                    </div>
                  </div>

                  <div className="flex flex-col items-center mb-6" data-id="element-803" data-mp="3deg4kea5vxd">
                    <img src="https://cdn.magicpatterns.com/uploads/n1oFfYN2XRMVeV75JV8L7q/48_hours_block_final.svg" alt="Gabriel Beltran — 48 Hrs AI Cash Machine" className="w-full max-w-[320px] h-auto" data-id="element-804" data-mp="3ujz57ykrytrr" />
                  </div>

                  <ul className="list-none space-y-0" data-id="element-805" data-mp="r3iae15kwjbp">
                    {[{
                  title: '48 Hrs AI Cash Machine',
                  accent: 'Cash Machine',
                  sub: 'El sistema central de 7 pasos para generar tu primer ingreso con AI.'
                }, {
                  title: 'AI Web Closer Academy',
                  accent: 'Web Closer',
                  sub: 'Incluye el software AI Website Sales Accelerator y los scripts de cierre.'
                }, {
                  title: 'Facebook Ads Expert',
                  accent: 'Facebook',
                  sub: 'Mastery Course & Systems: targeting, creación, optimización y escala.'
                }, {
                  title: 'Build AI Websites',
                  accent: 'AI Websites',
                  sub: 'AI Website Builder System + plantillas de sitios incluidas.'
                }, {
                  title: 'The Ultimate AI Creator Bundle',
                  accent: 'AI Creator',
                  sub: 'Produce imágenes y videos con AI de nivel profesional.'
                }, {
                  title: 'Custom AI Brains',
                  accent: 'AI Brains',
                  sub: 'Autopilot AI System: tu asistente AI preprogramado para cada paso.'
                }, {
                  bonus: true,
                  title: 'BONO: Llamadas en vivo todos los Lunes',
                  accent: 'BONO:',
                  sub: '12 meses de acompañamiento en directo, cada semana.'
                }, {
                  bonus: true,
                  title: 'BONO: Website Templates Con CRM Customized',
                  accent: 'BONO:',
                  sub: 'Plantillas de sitios web con CRM personalizado.'
                }, {
                  bonus: true,
                  title: 'BONO: Acceso a la comunidad privada de 48 Hrs AI Cash Machine en SKOOL',
                  accent: 'BONO:',
                  sub: 'Únete a la comunidad privada en la aplicación SKOOL.'
                }].map((item, i) => {
                  const [before, after] = item.accent ? (() => {
                    const idx = item.title.indexOf(item.accent);
                    return idx === -1 ? [item.title, ''] : [item.title.slice(0, idx), item.title.slice(idx + item.accent.length)];
                  })() : [item.title, ''];
                  return <li key={i} className="relative pl-9 py-3 border-b border-[#e2e8f0]" data-id="element-806" data-mp="2i2cjgk85tz7x" data-mpi={i}>
                          <span className="absolute left-0 top-3.5 w-5 h-5 rounded-full bg-[#0350AE] flex items-center justify-center" data-id="element-807" data-mp="smv4lu7n8dxk">
                            <span className="w-3.5 h-3.5 bg-white" style={{
                        WebkitMask: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><path fill='none' stroke='black' stroke-width='4' stroke-linecap='round' stroke-linejoin='round' d='M5 12h14m-6-6 6 6-6 6'/></svg>\") center/contain no-repeat",
                        mask: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><path fill='none' stroke='black' stroke-width='4' stroke-linecap='round' stroke-linejoin='round' d='M5 12h14m-6-6 6 6-6 6'/></svg>\") center/contain no-repeat"
                      }} data-id="element-808" data-mp="1umb9o5ztvkv" />
                          </span>
                          <p className="font-extrabold text-[16px] sm:text-[17px] leading-tight text-[#0f172a]" data-id="element-809" data-mp="3txvoi88d09yl">
                            {item.accent ? <>
                                {before}
                                <span className="text-[#dc2626]" data-id="element-810" data-mp="197emn8s4jyr8">
                                  {item.accent}
                                </span>
                                {after}
                              </> : item.title}
                          </p>
                          <p className="mt-1 text-[14px] leading-snug text-[#64748b]" data-id="element-811" data-mp="2ot86db0yzo0j">
                            {item.sub}
                          </p>
                        </li>;
                })}
                  </ul>

                  <div className="text-center mt-7 mb-2" data-id="element-812" data-mp="2a1pw7tusknp3">
                    <div className="font-['Anton'] tracking-[1px] text-[26px] sm:text-[32px] text-[#0f172a] mb-4" data-id="element-813" data-mp="2a3ar5kl0q8nh">
                      Precio exclusivo de hoy…
                    </div>
                    <div className="flex items-center justify-center gap-4 sm:gap-6 flex-wrap" data-id="element-814" data-mp="211tao13p84y9">
                      <span className="font-['Anton'] text-[56px] sm:text-[80px] leading-none text-[#0f172a] line-through decoration-[#dc2626] decoration-[5px]" data-id="element-815" data-mp="1w3rgol4qil80">
                        $9,370
                      </span>
                      <span className="font-['Anton'] text-[52px] sm:text-[76px] leading-none bg-gradient-to-b from-[#FD0200] to-[#AA0E13] text-[#ffffff] px-6 sm:px-8 pt-2 pb-1 rounded-xl" data-id="element-816" data-mp="3qe4vcxgqby61">
                        {priceLabel}
                      </span>
                    </div>
                    <div className="font-['Anton'] tracking-[0.5px] text-[18px] sm:text-[22px] text-[#475569] mt-5" data-id="element-817" data-mp="8bkcagz4qve0">
                      {priceSubLabel}
                    </div>
                  </div>

                  <div className="flex items-center gap-5 justify-center mt-8 max-w-[460px] mx-auto" data-id="element-818" data-mp="101f3kj6jsv5b">
                    <img src="https://cdn.magicpatterns.com/uploads/we8juYLVhboSuTkVksAsiQ/money_back.png" alt="Garantía de devolución 100% — Reembolso garantizado" className="shrink-0 w-[110px] sm:w-[124px] h-auto" data-id="element-819" data-mp="w1enyv7pqdwo" />
                    <p className="text-[15px] sm:text-[16px] text-[#0f172a] text-left leading-relaxed" data-id="element-820" data-mp="m8z6dz0bvrcb">
                      Si no quedas completamente satisfecho, te ofrecemos una{' '}
                      <b className="text-[#dc2626] font-bold" data-id="element-821" data-mp="1exeb9spqwt9x">
                        garantía de devolución total
                      </b>
                      . <span className="underline" data-id="element-822" data-mp="1r8x4qlcoqxtc">Sin preguntas.</span>
                    </p>
                  </div>
                </div>

                {/* RIGHT COLUMN: CTA */}
                <div className="lg:sticky lg:top-8" data-id="element-823" data-mp="20a53f4j460ny">
                  <div className="bg-[#ffffff] border border-[#9ca3af] shadow-[0_10px_30px_rgba(0,0,0,0.12)] rounded-xl p-6 sm:p-8 text-center" data-id="element-824" data-mp="20bya2jtq8kvy">
                    <h3 className="text-[20px] font-[800] mb-1 text-[#0f172a]" data-id="element-825" data-mp="2a8b4xl10d4sa">
                      Asegura tu acceso hoy
                    </h3>
                    <p className="text-[13px] text-[#64748b] mb-5" data-id="element-826" data-mp="2ezt361bncr9k">
                      El precio promocional por tiempo limitado expira en…
                    </p>

                    <div className="flex justify-center gap-2.5 mb-6" data-id="element-827" data-mp="2o0qyzwbxlsh2">
                      <div className="bg-[#fef2f2] border border-[#fca5a5] rounded-lg min-w-[64px] py-2 px-1.5 text-center" data-id="element-828" data-mp="2sz2asclsed1x">
                        <div className="font-['Anton'] text-[26px] text-[#dc2626] leading-none" data-id="element-829" data-mp="sw1ek4r59p5d">
                          {pad(hours)}
                        </div>
                        <div className="text-[9px] tracking-[1.5px] text-[#64748b] uppercase mt-1" data-id="element-830" data-mp="12mm8vfzsc7ih">
                          Horas
                        </div>
                      </div>
                      <div className="bg-[#fef2f2] border border-[#fca5a5] rounded-lg min-w-[64px] py-2 px-1.5 text-center" data-id="element-831" data-mp="xu2vok50fee7">
                        <div className="font-['Anton'] text-[26px] text-[#dc2626] leading-none" data-id="element-832" data-mp="17lz4ap4q2mwo">
                          {pad(minutes)}
                        </div>
                        <div className="text-[9px] tracking-[1.5px] text-[#64748b] uppercase mt-1" data-id="element-833" data-mp="1ljdye75x7iuz">
                          Min
                        </div>
                      </div>
                      <div className="bg-[#fef2f2] border border-[#fca5a5] rounded-lg min-w-[64px] py-2 px-1.5 text-center" data-id="element-834" data-mp="3nd3hqzeftizc">
                        <div className="font-['Anton'] text-[26px] text-[#dc2626] leading-none" data-id="element-835" data-mp="1xvpuppop7sr9">
                          {pad(seconds)}
                        </div>
                        <div className="text-[9px] tracking-[1.5px] text-[#64748b] uppercase mt-1" data-id="element-836" data-mp="22u145zb3018y">
                          Seg
                        </div>
                      </div>
                    </div>

                    {/* Price recap */}
                    <div className="flex items-center justify-center gap-3 mb-6 flex-wrap" data-id="element-837" data-mp="1f4ow52rhpz88">
                      <span className="font-['Anton'] text-[28px] text-[#64748b] line-through decoration-[#94a3b8]" data-id="element-838" data-mp="1k41g9fqz8s74">
                        $9,370
                      </span>
                      <span className="font-['Anton'] text-[40px] bg-gradient-to-b from-[#FD0200] to-[#AA0E13] text-[#ffffff] px-5 pt-1 rounded-md" data-id="element-839" data-mp="166wi01xi8xmf">
                        {priceLabel}
                      </span>
                    </div>

                    <a href="#" onClick={handleCheckout} style={{
                  backgroundImage: 'linear-gradient(180deg, #8bc63f 0%, #6cb52e 50%, #5aa525 100%)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -3px 8px rgba(58,110,12,0.45), 0 4px 10px rgba(0,0,0,0.18)'
                }} className="relative overflow-hidden flex items-center justify-center gap-2.5 w-full text-[#ffffff] font-extrabold text-[20px] tracking-tight p-4 rounded-lg transition-all duration-150 hover:-translate-y-0.5 hover:brightness-[1.04] active:translate-y-[2px]" data-id="element-840" data-mp="34kfwb323x3hy">
                      <span className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-lg bg-gradient-to-b from-white/35 to-transparent" data-id="element-841" data-mp="1s3eb1xi5elzg" />
                      <ShoppingCartIcon className="relative w-5 h-5 drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]" data-id="element-842" data-mp="1n54g9o0c5ned" />
                      <span className="relative [text-shadow:0_1px_2px_rgba(0,0,0,0.35)]" data-id="element-843" data-mp="1i3nhpz5bqd0y">
                        Obtén Acceso Ahora
                      </span>
                    </a>

                    <img src="https://cdn.magicpatterns.com/uploads/76cD3Rx79bU816uiFSdXqq/6814f798ca2000ae092e5f68.png" alt="Pago seguro con Stripe — Visa, Mastercard, American Express, Discover, Payoneer, Apple Pay, PayPal" className="w-full max-w-[220px] h-auto mx-auto mt-4" data-id="element-844" data-mp="195v9tg1xm7nl" />
                  </div>
                </div>
              </div>
            </div>
          </header>

          {/* QUOTE SECTION */}
          <section className="bg-gradient-to-b from-[#1e293b] to-[#0f172a] text-[#ffffff] text-center py-6 px-5" data-id="element-845" data-mp="2kpsfb94aubc">
            <p className="max-w-[880px] mx-auto text-[clamp(16px,2.2vw,22px)] font-light leading-[1.35] text-[#ffffff]" data-id="element-846" data-mp="363itiokplqom">
              "No necesitas más motivación. Necesitas un sistema que te dé
              resultados esta semana — y que dependa de tu ejecución, no de tu
              suerte."{' '}
              <b className="font-bold text-[#ffffff]" data-id="element-847" data-mp="2idyc4a47f1hg">— Gabriel Beltrán</b>
            </p>
          </section>

          {/* WHAT'S INCLUDED SECTION */}
          <section className="py-14" data-id="element-848" data-mp="2nbgk9yaedcoe">
            <div className="max-w-[1180px] mx-auto px-5" data-id="element-849" data-mp="2fcbwljvyedt0">
              <span className="block text-center bg-gradient-to-r from-[#FD0200] to-[#AA0E13] text-[#ffffff] font-['Poppins'] font-bold tracking-normal text-[clamp(14px,2.3vw,21px)] leading-tight max-w-[660px] mx-auto mb-2 py-3 px-7 rounded-[10px]" data-id="element-850" data-mp="2fasfjr2h3yq6">
                Esto es todo lo que recibes cuando aseguras tu
              </span>
              <div className="flex justify-center mt-3.5 mb-10" data-id="element-851" data-mp="2k5ej582bcmzp">
                <img src="https://cdn.magicpatterns.com/uploads/n1oFfYN2XRMVeV75JV8L7q/48_hours_block_final.svg" alt="Gabriel Beltran — 48 Hrs AI Cash Machine" className="w-full max-w-[550px] h-auto" data-id="element-852" data-mp="1wdzlhslhetus" />
              </div>

              <div className="space-y-5" data-id="element-853" data-mp="269kitdflv5y7">
                {[{
              title: '48 Hrs AI Cash Machine',
              tag: '(Sistema Central)',
              benefit: 'Tu primer ingreso con AI en un plan de 48 horas',
              img: 'https://cdn.magicpatterns.com/uploads/et7g9SXQ6R2S4NhvuLF2px/48_HRS.png',
              fallback: '48 HOURS\\nAI CASH MACHINE',
              bullets: ['El proceso completo de 7 pasos: Targets, Marketing, Leads, Ventas y Escala — ya armado.', 'Plantillas y prompts listos para copiar y ejecutar el mismo día.', 'Pasos concretos y medibles, sin relleno y sin teoría que no aplicas.']
            }, {
              title: 'AI Web Closer Academy',
              tag: '',
              benefit: 'Cierra clientes de negocios locales con AI',
              img: 'https://cdn.magicpatterns.com/uploads/vAtF5NAms3ZTbyUfkAw9K7/Ai_Web_Closer-2.png',
              fallback: 'AI WEB CLOSER\\nACADEMY',
              bullets: ['AI Prospecting Playbook + 90 Day AI Closing Blueprint paso a paso.', 'Acceso al software AI Website Sales Accelerator para gestionar tus cierres.', 'Local Business AI Scripts + AI Closer Elite Mastermind con la comunidad.']
            }, {
              title: 'Facebook Ads Expert',
              tag: '(Mastery Course & Systems)',
              benefit: 'Convierte la atención en clientes que pagan',
              img: 'https://cdn.magicpatterns.com/uploads/h2AGtNXx3iPYeMyn42Getp/Facebook_Ads_Expert-2.png',
              fallback: 'FACEBOOK\\nADS EXPERT',
              bullets: ['Targeting, creación de anuncios y lanzamiento — explicado paso a paso.', 'Estructura de campaña y de presupuesto que ya funciona en el mercado.', 'Optimización y escala basadas en datos reales, no en suposiciones.']
            }, {
              title: 'Build AI Websites',
              tag: '(AI Website Builder System)',
              benefit: 'Crea sitios web con AI en minutos, no en semanas',
              img: 'https://cdn.magicpatterns.com/uploads/uSEdrUzRP8WQMiL2k1kAgn/How_To_Websites.png',
              fallback: 'BUILD AI\\nWEBSITES',
              bullets: ['Sistema completo para construir sitios web profesionales usando AI.', 'Plantillas de sitios incluidas y listas para personalizar.', 'Un servicio que puedes vender a negocios locales desde el día uno.']
            }, {
              title: 'The Ultimate AI Creator Bundle',
              tag: '(Imágenes y Videos)',
              benefit: 'Produce imágenes y videos con AI de nivel profesional',
              img: 'https://cdn.magicpatterns.com/uploads/xdUczHdFzD4ia5JY8o9dbh/Ai_Image___video.png',
              fallback: 'THE ULTIMATE\\nAI CREATOR BUNDLE',
              bullets: ['AI Image Generation Mastery + Isolated AI Video Production.', "Prompt Engineering Guide y AI Creator's Workbook para producir rápido.", 'Automation & Workflow Secrets para crear contenido en serie.']
            }, {
              title: 'Custom AI Brains',
              tag: '(Autopilot AI System)',
              benefit: 'Tu asistente AI preprogramado para cada paso',
              img: 'https://cdn.magicpatterns.com/uploads/jiLDx9YCn8BDUyNwcdufrn/Custom_Lloyds-2.png',
              fallback: 'CUSTOM AI BRAINS\\nAUTOPILOT SYSTEM',
              bullets: ['AI a la medida que maneja automatización, datos y ejecución por ti.', 'Reportes y optimización sin que tengas que estar encima del proceso.', 'Diseñado para que el sistema trabaje mientras tú te enfocas en vender.']
            }].map((card, i) => <article key={i} className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-7 items-center bg-[#ffffff] border border-[#9ca3af] shadow-[0_10px_30px_rgba(0,0,0,0.12)] rounded-xl p-6" data-id="element-854" data-mp="3t4nprqdmgnf" data-mpi={i}>
                    <div className="flex items-center justify-center" data-id="element-855" data-mp="3q4jok0fzxnqk">
                      <img src={card.img.startsWith('http') ? card.img : `/${card.img}`} alt={card.title} className="w-full max-h-[340px] sm:max-h-[300px] md:max-h-[230px] object-contain" onError={e => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }} data-id="element-856" data-mp="hrm5l8tloyxf" />
                      <div className="hidden w-full max-w-[300px]" data-id="element-857" data-mp="2tgdq5v9o3rkq">
                        <ImageFallback text={card.fallback} data-id="element-858" data-mp="2xi8pwvby9wwe" />
                      </div>
                    </div>
                    <div className="text-center md:text-left" data-id="element-859" data-mp="2pyfy7k9vt7ss">
                      <h2 className="text-[23px] font-[800] mb-2.5 text-[#0f172a]" data-id="element-860" data-mp="2q0soqltpgqhg">
                        {card.title}{' '}
                        {card.tag && <span className="text-[#dc2626]" data-id="element-861" data-mp="2zxf9lam17hjh">{card.tag}</span>}
                      </h2>
                      <span className="block w-full text-center md:text-left bg-gradient-to-r from-[#FD0200] to-[#AA0E13] text-[#ffffff] font-['Poppins'] font-bold text-[15px] leading-tight px-7 py-3 rounded-full mb-3.5 shadow-sm" data-id="element-862" data-mp="2ux9e1jcn5aw4">
                        {card.benefit}
                      </span>
                      <ul className="space-y-1.5 text-left" data-id="element-863" data-mp="2gyjgk2lwt978">
                        {card.bullets.map((bullet, j) => <li key={j} className="relative pl-9 py-1 text-[14.5px] text-[#334155] leading-relaxed" data-id="element-864" data-mp="2c6rmvj7gincv" data-mpi={j}>
                            <span className="absolute left-0 top-1 w-5 h-5 rounded-full bg-gradient-to-b from-[#FD0200] to-[#AA0E13] text-[#ffffff] text-[12px] font-[800] flex items-center justify-center leading-none" data-id="element-865" data-mp="1ogxdxcq9dixw">
                              →
                            </span>
                            {bullet}
                          </li>)}
                      </ul>
                    </div>
                  </article>)}

                {/* BONUS CARDS */}
                {[{
              num: 1,
              title: 'Llamadas en Vivo Todos los Miércoles',
              benefit: 'Acompañamiento directo durante 12 meses',
              img: 'https://cdn.magicpatterns.com/uploads/ofaWhTtk4f8R4qHM8Vt98e/Weekly_Mastermind_Calls.png',
              fallback: 'BONO\\nLLAMADAS\\nEN VIVO',
              bullets: ['Sesión semanal en vivo para resolver tus dudas reales de ejecución.', 'Revisión de tus anuncios, tu oferta y tus cierres en tiempo real.', 'Acceso a la comunidad de quienes ya están ejecutando el sistema.']
            }, {
              num: 2,
              title: 'Website Templates Con CRM Customized',
              benefit: 'Plantillas de sitios web con CRM personalizado',
              img: 'https://cdn.magicpatterns.com/uploads/6d9LM81uEV7CZ3u3aGjF2e/Website_templates.png',
              fallback: 'BONO\\nWEBSITE TEMPLATES\\n+ CRM',
              bullets: ['Plantillas de sitios web profesionales listas para personalizar y publicar.', 'CRM personalizado integrado para capturar, organizar y dar seguimiento a tus leads.', 'Todo conectado en un solo lugar para que empieces a operar esta misma semana.']
            }, {
              num: 3,
              title: 'Comunidad Privada en SKOOL',
              benefit: 'Acceso a la comunidad privada de 48 Hrs AI Cash Machine',
              img: 'https://cdn.magicpatterns.com/uploads/pKwPbw4NUGCr5KcGEsv6kD/private_openrouter-generated-image-1781213810744.png',
              fallback: 'BONO\\nCOMUNIDAD\\nPRIVADA SKOOL',
              bullets: ['Acceso a la comunidad privada de 48 Hrs AI Cash Machine en la aplicación SKOOL.', 'Conecta con otros miembros que están ejecutando el mismo sistema.', 'Comparte avances, resuelve dudas y mantente acompañado en cada paso.']
            }].map((bonus, i) => <article key={`bonus-${i}`} className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-7 items-center bg-white border border-[#9ca3af] shadow-[0_10px_30px_rgba(0,0,0,0.12)] rounded-xl p-6" data-id="element-866" data-mp="2dp9pckgst7wg" data-mpi={i}>
                    <div className="flex items-center justify-center" data-id="element-867" data-mp="23quabfv1pp3b">
                      {bonus.img ? <img src={bonus.img} alt={bonus.title} className="w-full max-h-[340px] sm:max-h-[300px] md:max-h-[230px] object-contain" onError={e => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }} data-id="element-868" data-mp="1pzyv7ltyebra" /> : null}
                      <div className={`w-full max-w-[300px] ${bonus.img ? 'hidden' : ''}`} data-id="element-869" data-mp="1l02wa1045ail">
                        <ImageFallback text={bonus.fallback} data-id="element-870" data-mp="170uquqdcx65y" />
                      </div>
                    </div>
                    <div className="text-center md:text-left" data-id="element-871" data-mp="1b1n1no1vnj4c">
                      <h2 className="text-[23px] font-[800] mb-2.5 text-[#0f172a] leading-tight" data-id="element-872" data-mp="se4f0v20bv64">
                        <span className="text-[#dc2626]" data-id="element-873" data-mp="nfkriq7rmbbn">
                          BONO #{bonus.num}:
                        </span>{' '}
                        {bonus.title}
                      </h2>
                      <span className="block w-full text-center md:text-left bg-gradient-to-r from-[#FD0200] to-[#AA0E13] text-[#ffffff] font-['Poppins'] font-bold text-[15px] leading-tight px-7 py-3 rounded-full mb-3.5 shadow-sm" data-id="element-874" data-mp="wg96bn4zgfpl">
                        {bonus.benefit}
                      </span>
                      <ul className="space-y-1.5 text-left" data-id="element-875" data-mp="1k5bvdg1y5au4">
                        {bonus.bullets.map((bullet, j) => <li key={j} className="relative pl-9 py-1 text-[14.5px] text-[#334155] leading-relaxed" data-id="element-876" data-mp="1g48k76uug2z8" data-mpi={j}>
                            <span className="absolute left-0 top-1 w-5 h-5 rounded-full bg-gradient-to-b from-[#FD0200] to-[#AA0E13] text-[#ffffff] text-[12px] font-[800] flex items-center justify-center leading-none" data-id="element-877" data-mp="1u2ygwldr40n2">
                              →
                            </span>
                            {bullet}
                          </li>)}
                      </ul>
                    </div>
                  </article>)}
              </div>

              <hr className="border-0 border-t border-[#64748b] mt-12" data-id="element-878" data-mp="vpu9v9pokioy" />
            </div>
          </section>

          {/* FINAL CTA SECTION */}
          <section className="py-14 text-center border-t border-[#e2e8f0] bg-transparent" data-id="element-879" data-mp="15ijhfti41cf0">
            <div className="max-w-[1180px] mx-auto px-5" data-id="element-880" data-mp="1agbjhgcgyewx">
              <div className="flex justify-center mb-0 -mt-[52px]" data-id="element-881" data-mp="1aixq9drjcqe3">
                <img src="https://cdn.magicpatterns.com/uploads/n1oFfYN2XRMVeV75JV8L7q/48_hours_block_final.svg" alt="Gabriel Beltran — 48 Hrs AI Cash Machine" className="w-full max-w-[499px] h-auto" data-id="element-882" data-mp="1ejq0a988ezbf" />
              </div>

              <div className="mb-0 sm:-mb-8 flex justify-center" data-id="element-883" data-mp="3gbmanfiny34m">
                <img src="https://cdn.magicpatterns.com/uploads/gnQatCwJe9kqMyVCDBjSpQ/Whole_course.png" alt="48 Hrs AI Cash Machine — Bundle Completo" className="max-w-full sm:max-w-[1008px] h-auto" onError={e => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextElementSibling?.classList.remove('hidden');
            }} data-id="element-884" data-mp="3l9xjpo14w9ls" />
                <div className="hidden w-full max-w-[560px]" data-id="element-885" data-mp="2z0quc24uqrim">
                  <ImageFallback text="WHOLE BUNDLE" data-id="element-886" data-mp="2z2a9tqiooyjc" />
                </div>
              </div>

              <div className="text-center mt-0 mb-2" data-id="element-887" data-mp="i21xsnxfll0">
                <div className="font-['Poppins'] font-normal tracking-normal text-[22px] sm:text-[28px] text-[#0f172a] mb-4" data-id="element-888" data-mp="3romlkrbnjr80">
                  Precio exclusivo de hoy…
                </div>
                <div className="flex items-center justify-center gap-4 sm:gap-6 flex-wrap" data-id="element-889" data-mp="3iozlq9ykmth2">
                  <span className="font-['Anton'] text-[56px] sm:text-[80px] leading-none text-[#0f172a] line-through decoration-[#dc2626] decoration-[5px]" data-id="element-890" data-mp="2bamt21f4z3us">
                    $9,370
                  </span>
                  <span className="font-['Anton'] text-[52px] sm:text-[76px] leading-none bg-gradient-to-b from-[#FD0200] to-[#AA0E13] text-[#ffffff] px-6 sm:px-8 pt-2 pb-1 rounded-xl" data-id="element-891" data-mp="a85edzx8tehn">
                    {priceLabel}
                  </span>
                </div>
              </div>

              <a href="#" onClick={handleCheckout} className="relative overflow-hidden flex items-center justify-center gap-3 w-full max-w-[620px] mx-auto mt-5 bg-[#3FAC00] hover:bg-[#43B800] text-[#ffffff] font-extrabold text-[18px] sm:text-[24px] tracking-tight p-4 rounded-full transition-all duration-150 hover:-translate-y-0.5 active:translate-y-[2px] shadow-[0_4px_0_#2C7A00,0_1px_3px_rgba(0,0,0,0.07)] active:shadow-[0_2px_0_#2C7A00,0_1px_2px_rgba(0,0,0,0.07)]" data-id="element-892" data-mp="66s8xqy2bdb9">
                <span className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full bg-gradient-to-b from-white/35 to-transparent" data-id="element-893" data-mp="p3tfvhb6fnhu" />
                <ShoppingCartIcon className="relative w-6 h-6 shrink-0 drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]" data-id="element-894" data-mp="1hmgv4g3hs8b0" />
                <span className="relative [text-shadow:0_1px_2px_rgba(0,0,0,0.35)]" data-id="element-895" data-mp="1qzz8ptf670i3">
                  ¡Quiero el 48 Hrs AI Cash Machine!
                </span>
              </a>

              <div className="flex items-center gap-5 justify-center mt-8 max-w-[560px] mx-auto bg-white border-2 border-dotted border-[#D4AF37] rounded-2xl p-5 sm:p-7" data-id="element-896" data-mp="20rdeuk1mmyk7">
                <img src="https://cdn.magicpatterns.com/uploads/we8juYLVhboSuTkVksAsiQ/money_back.png" alt="Garantía de devolución 100% — Reembolso garantizado" className="shrink-0 w-[110px] sm:w-[124px] h-auto" data-id="element-897" data-mp="1826edfim3ywb" />
                <p className="text-[15px] sm:text-[16px] text-[#0f172a] text-left leading-relaxed" data-id="element-898" data-mp="1i02pi7bjutuf">
                  Si no quedas completamente satisfecho, te ofrecemos una{' '}
                  <b className="text-[#dc2626] font-bold" data-id="element-899" data-mp="1d3lzf7ksi5ep">
                    garantía de devolución total
                  </b>
                  . <span className="underline" data-id="element-900" data-mp="2ogxzd7dfi3vl">Sin preguntas.</span>
                </p>
              </div>
            </div>
          </section>
        </>}

      {/* FOOTER — compact; on the gated (all-blue) view it sits flush at the
                                                                                                                                                                                                                                bottom and stays small. */}
      <footer className={`bg-[#0f172a] py-4 text-[11px] text-[#94a3b8] ${revealed ? 'border-t border-[#1e293b] mt-0' : 'mt-auto'}`} data-id="element-901" data-mp="becqclpntly9">
        <div className="max-w-[1180px] mx-auto px-5" data-id="element-902" data-mp="6ei1enpg36fu">
          <div className="flex justify-center mb-5" data-id="element-903" data-mp="l4frxaauk887"></div>

          {/* Full-width gradient divider: light blue → dark blue */}
          <div className="relative left-1/2 right-1/2 -mx-[50vw] w-screen h-[3px] mb-5 bg-gradient-to-r from-[#0f172a] via-[#1d6fa5] to-[#0f172a]" data-id="element-904" data-mp="g52trteizeqb" />

          <p className="text-center text-[10px] text-[#94a3b8]" data-id="element-905" data-mp="u317qxoxp95a">
            <span className="text-[#64748b]" data-id="element-906" data-mp="p7c4aj9sn83p">
              © {new Date().getFullYear()} True Marketer LLC. Todos los
              derechos reservados.
            </span>
          </p>

          <LegalLinks containerClassName="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 mt-3 text-[#cbd5e1]" linkClassName="hover:text-[#36D9F4] mx-1.5 underline-offset-2 hover:underline" data-id="element-907" data-mp="1dr5rwqhgbqtj" />
        </div>
      </footer>

      {/* Floating WhatsApp button — hidden on /vsl-contact (it already has its
                          own in-page contact CTA); shown on every other VSL route. */}
      {!isContact && <FloatingWhatsApp link={floatingWaLink} data-id="element-908" data-mp="usteutnf5wso" />}

      {/* iPhone-style social-proof notifications — all VSL routes, from 18 min
                                                                                                of video PLAYBACK (armed via salesArmed). */}
      <SalesNotifications armed={salesArmed} contactMode={isContact} enabled={pageKey === 'vsl' || pageKey === 'vsl-2' || pageKey === 'vsl-3' || pageKey === 'vsl-3-shorter-demo' || pageKey === 'vsl-3-2' || pageKey === 'vsl-3-3' || pageKey === 'vsl-4' || pageKey === 'vsl-4-2' || pageKey === 'vsl-short-4min' || pageKey === 'vsl-short-4min-49' || pageKey === 'vsl-4-3-s' || pageKey === 'vsl-3-lead-2-147' || pageKey === 'vsl-3-2-shorter-demo' || pageKey === 'vsl-contact'} data-id="element-909" data-mp="71xj5lfuehg5" />

      {/* Early exit popup — only BEFORE the pitch opens; offers to send the
                                                                                          training over WhatsApp. After the offer reveals, the purchase-intent
                                                                                          popup takes over instead. */}
      <WhatsAppExitModal enabled={!revealed} whatsappLink={exitWaLink} onShow={() => trackCustomEvent('WhatsAppExitShown')} onAccept={() => {
      trackCustomEvent('WhatsAppLead', {
        source: 'exit_popup'
      });
      // Custom "Contact" event fired under the same pixel when the visitor
      // clicks to send the WhatsApp message from the exit popup.
      trackCustomEvent('Contact', {
        source: 'exit_popup'
      });
    }} data-id="element-910" data-mp="2nnkq34adf03h" />

      {/* Purchase-intent recovery popup — only AFTER the pitch opens (the offer
                                                                                          reveal), or opened immediately via ?exitdemo=1 for preview. */}
      <ExitIntentModal enabled={revealed || exitDemo} autoOpen={exitDemo}
    // $147 nets to $120 with 27OFF; the $97 offer has NO extra discount, so
    // it shows $97 and hides the coupon block (showDiscount={false}).
    priceLabel={isSubOffer ? '$49/mo' : is97Offer ? '$97' : is147Offer ? '$120' : priceLabel} showDiscount={!is97Offer && !isSubOffer} onAccept={goToCheckout} onShow={() => trackStandardEvent('InitiateCheckout', {
      value: priceValue,
      currency: 'USD'
    })} data-id="element-911" data-mp="l1gj93rh4a9" />

      {/* Disclaimer bottom-sheet popup */}
      {showDisclaimer && <div role="dialog" aria-modal="true" aria-label="Descargo de responsabilidad" className="fixed inset-0 z-[130] flex items-end justify-center" data-id="element-912" data-mp="2la8rc3bk97kh">
          <style data-id="element-913" data-mp="1pyr15m9d5hl5">{`@keyframes sheetUp{from{transform:translateY(100%)}to{transform:translateY(0)}}.sheet-up{animation:sheetUp .35s cubic-bezier(.16,1,.3,1) both}`}</style>
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowDisclaimer(false)} data-id="element-914" data-mp="1zyrodjacshum" />
          <div className="sheet-up relative w-full max-w-[680px] max-h-[80vh] flex flex-col bg-[#0f172a] border-t border-white/10 rounded-t-2xl shadow-[0_-20px_60px_rgba(0,0,0,0.5)]" data-id="element-915" data-mp="1uyn9tyt09x71">
            <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-white/10" data-id="element-916" data-mp="12arbyqrp9z8o">
              <h2 className="text-[14px] font-bold text-white" data-id="element-917" data-mp="1gzvw4di22djf">
                Descargo de responsabilidad y avisos legales
              </h2>
              <button type="button" onClick={() => setShowDisclaimer(false)} aria-label="Cerrar" className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full text-white/50 hover:bg-white/10 hover:text-white transition-colors" data-id="element-918" data-mp="1c72tbv65df9z">
                <span className="text-lg leading-none" data-id="element-919" data-mp="2v7lhvyqr1juj">×</span>
              </button>
            </div>
            <div className="px-5 py-5 overflow-y-auto text-[12px] leading-[1.6] text-[#94a3b8]" data-id="element-920" data-mp="2zxs7o1wzzaed">
              En True Marketer LLC creemos que el éxito es resultado del
              trabajo, la educación y la persistencia. Los ejemplos mostrados no
              deben considerarse típicos y no hay garantía de resultados. La
              información de este sitio es educativa por naturaleza y no
              constituye asesoría legal ni financiera. Al usar este sitio
              aceptas la responsabilidad total de tus propios resultados;
              nuestro equipo está aquí para apoyarte, pero siempre debes hacer
              tu propia investigación antes de tomar cualquier decisión de
              inversión o de negocio. Este sitio no está afiliado a Meta,
              Facebook ni a ninguna plataforma de terceros.
              <br data-id="element-921" data-mp="1yhuch4gx6c9y" />
              <br data-id="element-922" data-mp="1yidxiu1v9gs7" />
              <b className="text-[#e2e8f0]" data-id="element-923" data-mp="1yeq4dw0c2we0">True Marketer LLC</b>
              <br data-id="element-924" data-mp="40cjuzrt9bqf" />© {new Date().getFullYear()} True Marketer LLC. Todos los
              derechos reservados.
            </div>
          </div>
        </div>}
    </div>;
}